import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaTrophy, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';

const PageContainer = styled.div`
  padding: 2rem 0;
`;

const PageTitle = styled.h1`
  font-family: 'Press Start 2P', cursive;
  color: ${({ theme }) => theme.colors.accent};
  text-align: center;
  margin-bottom: 2rem;
  margin-top: 2em;
  text-shadow: 0 0 10px rgba(255, 230, 109, 0.5);
`;

const WinnersContainer = styled.div`
  background: rgba(0, 0, 0, 0.5);
  border-radius: 10px;
  padding: 2rem;
  max-width: 1000px;
  margin: 0 auto;
`;

const WinnersHeader = styled.div`
  display: grid;
  grid-template-columns: 100px 1fr 1fr 150px;
  padding: 1rem 1.5rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  margin-bottom: 1rem;
  font-weight: bold;
  text-transform: uppercase;
  font-size: 0.9rem;
  letter-spacing: 1px;
  
  @media (max-width: 768px) {
    grid-template-columns: 60px 1fr 1fr 100px;
    font-size: 0.8rem;
    padding: 0.8rem 1rem;
  }
`;

const WinnerRow = styled.div`
  display: grid;
  grid-template-columns: 100px 1fr 1fr 150px;
  padding: 1.2rem 1.5rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  margin-bottom: 0.8rem;
  align-items: center;
  transition: all 0.3s ease;
  border-left: 4px solid transparent;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: translateX(5px);
    border-left-color: ${({ theme }) => theme.colors.accent};
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 60px 1fr 1fr 100px;
    padding: 1rem;
    font-size: 0.9rem;
  }
`;

const GameId = styled.div`
  color: ${({ theme }) => theme.colors.accent};
  font-weight: bold;
`;

const WinnerAddress = styled.div`
  font-family: 'VT323', monospace;
  font-size: 1.1rem;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    word-break: break-all;
  }
`;

const PrizeAmount = styled.div`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: bold;
`;

const DateCell = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.9rem;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 2rem;
  gap: 1rem;
`;

const PageButton = styled.button`
  background: ${({ active, theme }) => 
    active ? theme.colors.primary : 'rgba(255, 255, 255, 0.1)'};
  color: ${({ active }) => (active ? '#000' : '#fff')};
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.primary};
    color: #000;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const NavigationButton = styled(PageButton)`
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  
  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.primary};
    color: #000;
  }
`;

const NoWinners = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 1.2rem;
`;

// Fetch winners from backend

const WinnersPage = () => {
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;
  const baseUrl = process.env.REACT_APP_SITE_URL || (typeof window !== 'undefined' ? window.location.origin : '');

  // Format address to show first and last 4 characters
  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  };

  // Format date as "15 Jun 2023"
  const formatDate = (dateString) => {
    const d = new Date(dateString);
    const day = String(d.getDate()).padStart(2, '0');
    const month = d.toLocaleString('en-US', { month: 'short' });
    const year = d.getFullYear();
    return `${day} ${month} ${year}`;
  };

  // Fetch winners data
  useEffect(() => {
    const fetchWinners = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${process.env.REACT_APP_API_URL}/games/winners?limit=50`);
        if (!res.ok) throw new Error(`Failed to load winners: ${res.status}`);
        const data = await res.json();
        // Expected payload: [{ gameId, walletAddress, prize, endTxHash, dateTime }]
        setWinners(data);
      } catch (error) {
        console.error('Error fetching winners:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchWinners();
  }, []);

  // Calculate pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentWinners = winners.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(winners.length / itemsPerPage) || 1;

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Go to next page
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Go to previous page
  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Generate page numbers
  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  return (
    <PageContainer>
      <Helmet>
        <title>Past Winners — YooWin</title>
        <meta name="description" content="See past YooWin raffle winners and prize history." />
        <link rel="canonical" href={`${baseUrl}/winners`} />
        <meta property="og:title" content="Past Winners — YooWin" />
        <meta property="og:description" content="See past YooWin raffle winners and prize history." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${baseUrl}/winners`} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Past Winners — YooWin',
          url: `${baseUrl}/winners`
        })}</script>
      </Helmet>
      <PageTitle>Past Winners</PageTitle>
      
      <WinnersContainer>
        <WinnersHeader>
          <div>Game ID</div>
          <div>Winner</div>
          <div>Prize</div>
          <div>Date</div>
        </WinnersHeader>
        
        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>Loading winners...</div>
        ) : winners.length === 0 ? (
          <NoWinners>
            <FaTrophy size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
            <div>No winners yet. Be the first to win!</div>
          </NoWinners>
        ) : (
          <>
            {currentWinners.map((w, idx) => {
              const prizeText = typeof w.prize === 'number' && !Number.isNaN(w.prize)
                ? `${w.prize.toFixed(2)} ETH`
                : (w.prize ? String(w.prize) : '-');
              return (
                <WinnerRow key={`${w.gameId}-${idx}`}>
                  <GameId>#{w.gameId}</GameId>
                  <WinnerAddress title={w.walletAddress}>
                    {formatAddress(w.walletAddress)}
                  </WinnerAddress>
                  <PrizeAmount>{prizeText}</PrizeAmount>
                  <DateCell>{formatDate(w.dateTime)}</DateCell>
                </WinnerRow>
              );
            })}
            
            {totalPages > 1 && (
              <Pagination>
                <NavigationButton 
                  onClick={prevPage} 
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                >
                  <FaChevronLeft />
                </NavigationButton>
                
                {pageNumbers.map((number) => (
                  <PageButton
                    key={number}
                    active={number === currentPage}
                    onClick={() => paginate(number)}
                  >
                    {number}
                  </PageButton>
                ))}
                
                <NavigationButton 
                  onClick={nextPage} 
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                >
                  <FaChevronRight />
                </NavigationButton>
              </Pagination>
            )}
          </>
        )}
      </WinnersContainer>
    </PageContainer>
  );
};

export default WinnersPage;
